import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { Logger } from '@calendly/logging-service'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { fetchCurrentSubscription } from 'features/billing/actions/fetch_current_subscription'
import { idSelector } from 'selectors/current_user'

const isValidPaypalToken = (token) => {
  if (!token || typeof token !== 'string') {
    return false
  }

  if (token.length < 13) {
    return false
  }

  if (!token.startsWith('BA-') && !token.startsWith('EC-')) {
    Logger.warn('PayPal token format unexpected', {
      tokenPrefix: token.substring(0, 3),
      tokenLength: token.length
    })
  }

  return true
}

/**
 * Confirms a PayPal payment session for an existing subscription payment method change
 * (card → PayPal or PayPal → PayPal).
 *
 * @param {string} token - PayPal billing agreement token from authentication
 */
export const completePaypalPaymentMethodChange = createAsyncThunk(
  'billing/completePaypalPaymentMethodChange',
  async (token, { dispatch, getState, rejectWithValue }) => {
    if (!isValidPaypalToken(token)) {
      Logger.error('PayPal payment method change failed: invalid token format', {
        tokenLength: token?.length,
        tokenPrefix: token ? `${token.substring(0, 10)}...` : token
      })
      return rejectWithValue(
        I18n.t('errors.billing.paypal.invalid_token', {
          default: 'Invalid payment token. Please try again.'
        })
      )
    }

    const userId = idSelector(getState())

    try {
      const [success, data] = await httpRequest({
        url: '/api/billing/subscription/payment_sessions/confirm',
        method: 'POST',
        data: {
          session_id: token,
          payment_method_type: 'paypal'
        }
      })

      if (!success) {
        const errorMessage = data?.message || data?.error?.message || ''
        const defaultMessage = I18n.t('errors.billing.paypal.unexpected_error', {
          default: 'An unexpected error occurred. Please try again.'
        })
        const finalMessage = errorMessage.trim() !== '' ? errorMessage : defaultMessage

        Logger.error('PayPal payment method change confirm failed', {
          error: finalMessage,
          token: `${token.substring(0, 10)}...`,
          userId
        })
        return rejectWithValue(finalMessage)
      }

      await dispatch(fetchCurrentSubscription())
      return true
    } catch (error) {
      Logger.error('PayPal payment method change confirm failed', {
        error: error.message,
        stack: error.stack,
        token: `${token.substring(0, 10)}...`,
        userId
      })
      return rejectWithValue(
        error.message
        || I18n.t('errors.billing.paypal.unexpected_error', {
          default: 'An unexpected error occurred. Please try again.'
        })
      )
    }
  }
)
